// Feature #379 — a small green-accented confirmation pinned to the top of the
// screen. For "that worked" acknowledgements where the action's own UI (a
// modal, a button) has just disappeared. Dismisses itself; the caller owns the
// message and clears it in onDismiss.
import React, { useEffect } from 'react'

interface Props {
  /** Null/empty renders nothing. */
  message: string | null
  onDismiss: () => void
  /** Milliseconds before it goes away on its own. */
  durationMs?: number
}

export const Toast: React.FC<Props> = ({ message, onDismiss, durationMs = 6000 }) => {
  useEffect(() => {
    if (!message) return
    const timer = window.setTimeout(onDismiss, durationMs)
    return () => window.clearTimeout(timer)
  }, [message, durationMs, onDismiss])

  if (!message) return null

  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        position: 'fixed',
        top: '16px',
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 2000,
        maxWidth: 'min(560px, calc(100vw - 32px))',
        display: 'flex',
        alignItems: 'flex-start',
        gap: '12px',
        padding: '12px 16px',
        backgroundColor: '#f0fdf4',
        border: '1px solid #bbf7d0',
        borderLeft: '4px solid #16a34a',
        borderRadius: '6px',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.12)',
        color: '#166534',
        fontSize: '14px',
        fontFamily: 'var(--font-family)',
        lineHeight: 1.4,
      }}
    >
      <span style={{ flex: 1 }}>{message}</span>
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss"
        style={{
          background: 'none',
          border: 'none',
          color: '#166534',
          cursor: 'pointer',
          fontSize: '16px',
          lineHeight: 1,
          padding: 0,
        }}
      >
        ×
      </button>
    </div>
  )
}

export default Toast
